
import { Component, Vue, Prop, Watch, Inject, Emit } from "vue-property-decorator";
import { namespace } from "vuex-class";
import TextHighlight from "vue-text-highlight";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce } from "lodash";

import api from "@/services/api.service";
import MapLayersSelect from "./MapLayersSelect.vue";
import ViewsEventBus from "@/services/views/viewsEventBus";
import Tree from "../Tree.vue";
import { SiteTreeNode } from "@/store/eventqueue/types";
import { ViewObject } from "@/store/views/types.ts";

const Eventqueue = namespace("eventqueue");
const SMCameras = namespace("siteMonitorCameras");
const ViewState = namespace("views");
const Devices = namespace("devices");

@Component({
	components: {
		tree: Tree,
		"map-layers": MapLayersSelect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"text-highlight": TextHighlight
	}
})
export default class ViewSidebar extends Vue {

	@SMCameras.Action fetchAllDevicesByGroup: any;

	@ViewState.Mutation setSideBarPinned: any;
	@ViewState.Getter getSideBarPinned: boolean;
	@ViewState.Getter("getSelectedView") selectedView: ViewObject;

	@Eventqueue.Action sitesTreeGet: any;
	@Eventqueue.Mutation toggleSiteTreeNodeOpen: any;
	@Eventqueue.Mutation setSiteNodeContents: any;
	@Eventqueue.Getter("getSiteTree") tree: SiteTreeNode[];

	@Devices.Action deviceSearchAndAppendToGroupTree: ({ query: string, deviceTypeId: number }) => Promise<void>;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	$refs!: {
		scrollBar: any;
	};

	private isCamerasCardExpanded = true;
	private searchQuery: string = "";
	private deviceSearchResults: any[] = [];
	private selectedCamera: any = {};

	private querySearchStatus: boolean = false;

	public mounted() {
		this.sitesTreeGet();
		this.eventBus.$on("endCamera", this.hideCameraSelect);
	}

	private debouncedDeviceSearch = debounce(async () => {
		await this.querySearch();
	}, 1000);


	private async querySearch() {
		let searchQuery = this.getSearchQuery();
		if (searchQuery !== "")
		{
			await this.deviceSearchAndAppendToGroupTree({ query: searchQuery, deviceTypeId: 1 });
			this.querySearchStatus = false;
			return;
		}
		this.querySearchStatus = false;
	}

	@Watch("searchQuery")
	private async onSearchQueryChange(value: string, oldValue: string) {
		if (value === "") {
			await this.sitesTreeGet();
			this.$refs.scrollBar.update();
			this.$refs.scrollBar.scrollTop = 0;
			this.querySearchStatus = false;
			return;
		}
		if (value != oldValue) {
			this.querySearchStatus = true;
			await this.debouncedDeviceSearch();
			this.$refs.scrollBar.update();
			this.$refs.scrollBar.scrollTop = 0;
		}
	}

	// used by debouncedDeviceSearch to get the current searchQuery value
	// fixes an issue when search input is cleared where debouce would search against the last entered query
	private getSearchQuery() {
		return this.searchQuery;
	}

	private treeNodeOpened(nodeGroup: any) {
		this.loadZonesAndDevicesForNode(nodeGroup);
	}

	private treeNodeSelected(node: any) {
		this.toggleSiteTreeNodeOpen(node.node);
		this.treeNodeOpened(node.node);
	}

	private async loadZonesAndDevicesForNode(selectedAreaNode: any) {
		if (selectedAreaNode.loadingContents) {
			selectedAreaNode.contentsOpen = !selectedAreaNode.contentsOpen;
		} else {
			if (selectedAreaNode.contents == null) {
				selectedAreaNode.loadingContents = true;
				selectedAreaNode.contentsOpen = true;
				selectedAreaNode.isOpen = true;
				const devices = (
					await this.fetchAllDevicesByGroup({
						groupID: selectedAreaNode.group.groupID,
						pageSize: 500
					})
				).filter(device => device.deviceTypeID === 1);
				this.setSiteNodeContents({
					treeNode: selectedAreaNode,
					content: {
						devices
					}
				});
				if (selectedAreaNode.contents.devices.length == 0) {
					selectedAreaNode.contentsOpen = true;
				}
				else {
					selectedAreaNode.contentsOpen = true;
				}
				selectedAreaNode.loadingContents = false;
			} else {
				selectedAreaNode.contentsOpen = !selectedAreaNode.contentsOpen;
			}
		}
		if(!this.selectedView.locked){
			this.eventBus.$emit("beginGroupSelect", selectedAreaNode.group.groupID);
		}
	}

	// Raise event to show camera
	private async showAreaCamera(camera) {
		this.selectedCamera = camera;
		if(!this.selectedView.locked){
			this.eventBus.$emit("beginCamera", camera);
		}
	}

	private get sideBarPinned() {
		return this.getSideBarPinned;
	}

	private set sideBarPinned(state: boolean) {
		this.setSideBarPinned(state);
	}

	private hideCameraSelect() {
		this.selectedCamera = {};
	}

	private get anyVisibleSitesOrCameras() {
		return this.tree.reduce((curr, prev) => curr || prev.show, false);
	}
}
